import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Anchor, Container } from 'components/base';
import {
  CloudinaryPicture,
  HtmlContent,
  ScriptMarkupFaq,
  WIDTH_LADDERS,
  faqContentToItems,
  getCloudinarySrcSet,
  getCloudinaryUrl,
} from 'components/rich';
import useDataContext from 'data/context/data';
import contentApi from 'data/api/content';
import useClaimedCoupon from 'ecommerce/context/claimedCoupon';

import { Head, Layout } from '../components/layout';
import LandingProductTile from '../components/custom/landingProductTile';
import PdpIcon from '../components/product/pdpIcon';
import ProductFaq from '../components/product/productFaq';
import tracker from '../lib/tracker';
import { productPrice } from '../lib/landingPrice';
import useInView from '../lib/useInView';
import SETTINGS from '../settings';

// Photo books hub (docs/photo-books-hub.md). Explicit page file, so it wins over the
// category route in [...params].js. Title, meta, H1, hero copy, the "Choose" H2, the
// editorial block and the first FAQ items come from content/categories/photo-books.md.
const CATEGORY_ID = 'photo-books';
const CAMPAIGN = 'photo_books_hub';
const BOOKS_ID = 'books';

// Mini photo books page (the 6 × 6 Softcover). Anchors say "mini photo book", never "6x6",
// which stays this hub's query (docs/photo-books-hub.md).
const MINI_HREF = '/mini-photo-books';
const MINI_PRODUCT = 'mini-photo-books';

const APP_STORE_HREF = (() => {
  const url = new URL(SETTINGS.PHOTO_BOOKS_APP_STORE_URL);
  url.searchParams.set('utm_source', 'website');
  url.searchParams.set('utm_medium', 'category');
  url.searchParams.set('utm_campaign', CAMPAIGN);
  return url.href;
})();

const image = name => `landing/photo-books/${name}`;

const HERO_IMAGE = image('hc-hands-open');
const HERO_IMAGE_SIZES = '(min-width: 992px) 372px, (min-width: 768px) 45vw, calc(100vw - 48px)';

// Card titles keep the anchor text of the links the hub had before the redesign.
const BOOKS = [
  {
    productId: 'softcover-books',
    href: '/softcover-books',
    title: 'Softcover photo books',
    imageKey: image('sc-stack-wood'),
    tag: { label: 'Easy layouts' },
    line: 'Light, flexible, easy to love.',
    summary: '6x6 or 8x8 in · 26–100 pages',
    specs: [
      { icon: 'ruler', label: '6x6 or 8x8 in square' },
      { icon: 'layers', label: '26 pages, up to 100' },
      { icon: 'book', label: 'Flexible photo cover' },
      { icon: 'smartphone', label: 'iPhone app or web' },
    ],
    goodFor: 'A trip, a pet, a little gift',
    ctaLabel: 'Make a softcover book',
  },
  {
    productId: 'hardcover-book',
    href: '/hardcover-book',
    title: 'Hardcover photo book',
    imageKey: image('hc-cover-held'),
    tag: { label: 'Easy layouts' },
    line: 'A sturdy cover. The same easy process.',
    summary: '8x8 in · 26–100 pages',
    specs: [
      { icon: 'ruler', label: '8x8 in square' },
      { icon: 'layers', label: '26 pages, up to 100' },
      { icon: 'book', label: 'Hard photo cover' },
      { icon: 'smartphone', label: 'iPhone app or web' },
    ],
    goodFor: 'An anniversary, family moments',
    ctaLabel: 'Make a hardcover book',
  },
  {
    productId: 'premium-hardcover-books',
    href: '/premium-hardcover-books',
    title: 'Premium hardcover photo books',
    imageKey: image('pr-photo-cover'),
    tag: { label: 'Creative freedom', tone: 'terracotta' },
    line: 'A luxe book, made your way.',
    summary: '11x8.5 or 13x10 in · 20–350 pages',
    specs: [
      { icon: 'ruler', label: '11x8.5 or 13x10 in landscape' },
      { icon: 'layers', label: '20 pages, up to 350' },
      { icon: 'book', label: 'Photo or vegan leather, gold foil' },
      { icon: 'monitor', label: 'Web, on a computer or tablet' },
    ],
    goodFor: 'Weddings, big trips, milestones',
    ctaLabel: 'Design a premium book',
    ctaOutline: true,
  },
];

const TRUST_POINTS = [
  { icon: 'leaf', label: 'FSC-certified premium matte paper' },
  { icon: 'type', label: 'Captions on the cover and pages' },
  { icon: 'copy', label: 'Reorder copies for family from your account' },
];

const GUIDE_HREF = '/how-to-make-a-photo-book';

// Same before/after spreads as the guide (pages/how-to-make-a-photo-book.js).
const GUIDE_SPREADS = [
  {
    kind: 'before',
    badge: 'Before',
    src: '/static/img/photo-book-guide-before.webp',
    alt: 'A busy photo book spread with four near copies of one beach photo and three small photos',
  },
  {
    kind: 'after',
    badge: 'After',
    src: '/static/img/photo-book-guide-after.webp',
    alt: 'The same beach day as a calm spread, one photo on the full left page and two wide views on the right',
  },
];

const GUIDE_POINTS = [
  { label: 'Six checks before you order' },
  { label: 'All 13 page layouts and 4 covers' },
  { label: 'Before and after spreads', desktopOnly: true },
  { label: 'Captions that add something' },
];

const STEPS = [
  {
    title: 'Pick your photos',
    description:
      'Choose them right from your Camera Roll in the iPhone app, or upload from your computer. HEIC, JPG and PNG all work.',
    imageKey: image('hc-couch-open'),
  },
  {
    title: 'Choose a layout, add a caption',
    description:
      'Pick a ready-made layout and add a place, a date, or a few words. No design skills needed.',
    imageKey: image('sc-captions-open'),
    link: { href: GUIDE_HREF, label: 'How to make a photo book look polished', cta: 'guide' },
  },
  {
    title: 'We print it and ship it',
    description: 'Printed on premium matte paper, bound, and shipped to your door.',
    imageKey: image('hc-pages-macro'),
    link: { href: '/shipping', label: 'See production times and shipping' },
  },
];

const IDEAS = [
  'Weekend getaway',
  'Grandkids brag book',
  'Pet of the year',
  'Year in review',
  'Baby’s first year',
  'Wedding album',
];

// Real quotes from the softcover and hardcover product pages.
const QUOTES = [
  {
    quote:
      'The quality is great and I love that the photo books are small enough to keep in your handbag, because you know grandmas love to show off photos of their grandkids when they’re out and about!',
    handle: '@littlehiccups',
    product: 'Softcover photo book',
    // The 6-inch book in hands, the softcover PDP's photo for this quote.
    imageKey: 'softcover-books/2',
    prefix: 'products/',
    widths: WIDTH_LADDERS.products,
    link: { label: 'See mini photo book ideas', href: MINI_HREF },
  },
  {
    quote:
      'In love with my photo book from Parabo Press. My photos look fantastic and the quality is impressive.',
    handle: '@jeneraliln',
    product: '8x8 Hardcover photo book',
    imageKey: image('hc-cover-held'),
  },
];

const trackCtaClick = (cta, location, product) => () => {
  tracker.onCustomEvent(`${CAMPAIGN}_cta_click`, { cta, location, product });
};

const pricedLabel = ({ price, priceFrom }) => (priceFrom ? `From ${price}` : price);

const AppStoreBadge = ({ location }) => (
  <Anchor
    href={APP_STORE_HREF}
    className="pbh-app-badge"
    targetBlank
    onClick={trackCtaClick('app_store', location)}
  >
    <img
      src="/static/img/app-store-badge.svg"
      alt="Download Parabo Press on the App Store"
      width="144"
      height="48"
    />
  </Anchor>
);

AppStoreBadge.propTypes = {
  location: PropTypes.string.isRequired,
};

// Desktop handoff to the phone. The code opens the same App Store page as the badge (without
// UTM parameters), and the tile links there too for keyboard and screen reader users.
const QrHandoff = ({ location }) => (
  <Anchor
    href={APP_STORE_HREF}
    className="pbh-qr"
    targetBlank
    onClick={trackCtaClick('app_store', location)}
  >
    <span className="pbh-qr-code">
      <img
        src="/static/img/photo-books-app-qr.svg"
        alt="QR code for the Parabo Press app on the App Store"
        width="68"
        height="68"
      />
    </span>
    <span className="pbh-qr-label">
      Scan with
      <br />
      your iPhone
    </span>
  </Anchor>
);

QrHandoff.propTypes = {
  location: PropTypes.string.isRequired,
};

const IconList = ({ items, className }) => (
  <ul className={className}>
    {items.map(({ icon, label }) => (
      <li key={label}>
        <PdpIcon name={icon} size={16} />
        {label}
      </li>
    ))}
  </ul>
);

IconList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({ icon: PropTypes.string, label: PropTypes.string }))
    .isRequired,
  className: PropTypes.string,
};

const Picture = ({ imageKey, prefix, widths = WIDTH_LADDERS.landing, sizes, alt, priority }) => (
  <CloudinaryPicture
    imageKey={imageKey}
    prefix={prefix}
    widths={widths}
    sizes={sizes}
    alt={alt}
    priority={priority}
    loading={priority ? undefined : 'lazy'}
  />
);

Picture.propTypes = {
  imageKey: PropTypes.string.isRequired,
  prefix: PropTypes.string,
  widths: PropTypes.arrayOf(PropTypes.number),
  sizes: PropTypes.string.isRequired,
  alt: PropTypes.string,
  priority: PropTypes.bool,
};

const PhotoBooksPage = ({ content }) => {
  const dataContext = useDataContext();
  const { previewSummary } = useClaimedCoupon();
  const booksRef = useRef();
  const booksInView = useInView(booksRef);

  const category = dataContext.getCategory(CATEGORY_ID);
  const books = BOOKS.map(book => ({
    ...book,
    ...productPrice(dataContext.getProduct(book.productId), previewSummary),
  }));
  const [softcover, hardcover] = books;

  return (
    <>
      <Head
        title={category.seoTitle ?? category.name}
        description={category.seoDescription ?? category.shortDescription}
        image={content.seoUrl || getCloudinaryUrl({ imageKey: category.image })}
        heroImage={getCloudinaryUrl({ imageKey: HERO_IMAGE })}
        heroImageSrcSet={getCloudinarySrcSet({ imageKey: HERO_IMAGE, widths: WIDTH_LADDERS.landing })}
        heroImageSizes={HERO_IMAGE_SIZES}
      />

      {content.faq && <ScriptMarkupFaq items={faqContentToItems(content.faq)} />}

      <Layout>
        <div className="pbh">
          {/* One DOM for both layouts: phones reorder the hero and the book picker with CSS. */}
          <div className="pbh-top">
            <section className="pbh-hero">
              <Container className="pbh-hero-inner">
                <div className="pbh-hero-copy">
                  <nav aria-label="Breadcrumb" className="pbh-breadcrumb">
                    <ol>
                      <li>
                        <Anchor href="/">Home</Anchor>
                        <PdpIcon name="chevronRight" size={12} />
                      </li>
                      <li aria-current="page">{category.name}</li>
                    </ol>
                  </nav>
                  <h1 className="pbh-h1">{category.heroTitle ?? category.name}</h1>
                  <p className="pbh-lead">{category.shortDescription}</p>
                  <IconList
                    className="pbh-facts"
                    items={[
                      { icon: 'tag', label: `From ${softcover.price}` },
                      { icon: 'layers', label: '26 pages included' },
                      { icon: 'smartphone', label: 'Make it on iPhone or web' },
                    ]}
                  />
                  <div className="pbh-hero-actions">
                    <a
                      href={`#${BOOKS_ID}`}
                      className="pbh-btn"
                      onClick={trackCtaClick('choose_book', 'hero')}
                    >
                      Choose your book
                      <PdpIcon name="arrowDown" size={16} />
                    </a>
                    <AppStoreBadge location="hero" />
                  </div>
                </div>

                <div className="pbh-hero-photos">
                  <div className="pbh-hero-photo pbh-hero-photo--main">
                    <Picture imageKey={HERO_IMAGE} sizes={HERO_IMAGE_SIZES} priority />
                    <p className="pbh-chip" aria-hidden="true">
                      <span className="pbh-chip-prefix">8x8 Hardcover</span>
                      <span className="pbh-chip-price">{hardcover.price}</span>
                    </p>
                  </div>
                  <div className="pbh-hero-photo pbh-hero-photo--top">
                    <Picture imageKey={image('sc-captions-open')} sizes="212px" alt="" />
                  </div>
                  <div className="pbh-hero-photo pbh-hero-photo--bottom">
                    <Picture imageKey={image('pr-leather-foil')} sizes="212px" alt="" />
                  </div>
                </div>
              </Container>
            </section>

            <section id={BOOKS_ID} ref={booksRef} className="pbh-books">
              <Container className="pbh-books-inner">
                <div className="pbh-books-head">
                  <div className="pbh-books-heading">
                    <p className="pbh-eyebrow">Three books. Your kind of creating.</p>
                    {content.intro && <HtmlContent className="pbh-books-h2" html={content.intro} />}
                  </div>
                  <p className="pbh-books-intro">
                    Softcover and Hardcover use ready-made layouts, so you can finish in an evening.
                    Premium lets you design every page.
                  </p>
                </div>

                <div className="pbh-tiles">
                  {books.map(({ productId, ...book }) => (
                    <LandingProductTile
                      key={productId}
                      {...book}
                      onClick={trackCtaClick('product', 'card', productId)}
                    />
                  ))}
                </div>

                <p className="pbh-app-row">
                  <span>Softcover and Hardcover in the app</span>
                  <AppStoreBadge location="picker" />
                </p>
              </Container>
            </section>

            {/* Under the tiles on desktop, after the hero copy on phones. */}
            <Container className="pbh-trust-wrap">
              <IconList className="pbh-trust" items={TRUST_POINTS} />
            </Container>
          </div>

          <section className="pbh-section pbh-how">
            <Container>
              <div className="pbh-section-head pbh-section-head--center">
                <p className="pbh-eyebrow">How it works</p>
                <h2 className="pbh-h2">From Camera Roll to coffee table</h2>
                <p className="pbh-sub">
                  Making a photo book shouldn’t become another project you never finish.
                </p>
              </div>

              <div className="pbh-how-photo">
                <Picture imageKey={image('sc-captions-open')} sizes="calc(100vw - 48px)" alt="" />
              </div>

              <ol className="pbh-steps">
                {STEPS.map((step, index) => (
                  <li key={step.title} className="pbh-step">
                    <div className="pbh-step-photo">
                      <Picture
                        imageKey={step.imageKey}
                        sizes="(min-width: 992px) 384px, 33vw"
                        alt=""
                      />
                    </div>
                    <div className="pbh-step-text">
                      <span className="pbh-step-number" aria-hidden="true">
                        {index + 1}
                      </span>
                      <div className="pbh-step-copy">
                        <h3 className="pbh-step-title">{step.title}</h3>
                        <p className="pbh-step-desc">{step.description}</p>
                        {step.link && (
                          <Anchor
                            href={step.link.href}
                            className="pbh-arrow-link"
                            onClick={step.link.cta && trackCtaClick(step.link.cta, 'how')}
                          >
                            {step.link.label}
                            <PdpIcon name="arrowRight" size={14} />
                          </Anchor>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </Container>
          </section>

          <section className="pbh-section pbh-guide">
            <Container className="pbh-guide-inner">
              <div className="pbh-guide-intro">
                <p className="pbh-eyebrow pbh-eyebrow--muted">Photo book guide</p>
                <h2 className="pbh-h2">How to make a photo book that looks polished</h2>
                <p className="pbh-body">
                  Not sure how to lay out the pages? See the same photos as a busy spread and a calm
                  one
                  <span className="lnd-desk">
                    , every layout drawn to scale, and six checks to run before you order.
                  </span>
                  <span className="lnd-phone">, plus every layout drawn to scale.</span>
                </p>
              </div>
              <div className="pbh-guide-more">
                <ul className="pbh-guide-points">
                  {GUIDE_POINTS.map(point => (
                    <li key={point.label} className={point.desktopOnly ? 'lnd-desk' : undefined}>
                      <PdpIcon name="check" size={16} />
                      {point.label}
                    </li>
                  ))}
                </ul>
                <Anchor
                  href={GUIDE_HREF}
                  className="pbh-btn pbh-btn--outline"
                  onClick={trackCtaClick('guide', 'guide')}
                >
                  Read the guide
                  <PdpIcon name="arrowRight" size={18} />
                </Anchor>
              </div>
              <div className="pbh-guide-visual">
                {GUIDE_SPREADS.map(spread => (
                  <figure key={spread.kind} className={`pbh-guide-spread pbh-guide-spread--${spread.kind}`}>
                    <figcaption className="pbh-guide-badge">{spread.badge}</figcaption>
                    <img src={spread.src} alt={spread.alt} width="1120" height="561" loading="lazy" />
                  </figure>
                ))}
                <aside className="pbh-guide-tip lnd-desk">
                  <p className="pbh-guide-tip-label">
                    <PdpIcon name="lightbulb" size={16} />
                    Advisor tip
                  </p>
                  <p>Give your best photo a full page. It tells the reader this moment matters.</p>
                </aside>
              </div>
            </Container>
          </section>

          <section className="pbh-section pbh-library">
            <Container className="pbh-library-inner">
              <div className="pbh-library-photo">
                <Picture
                  imageKey={image('sc-stack-blue')}
                  sizes="(min-width: 992px) 600px, calc(100vw - 48px)"
                />
              </div>
              <div className="pbh-library-copy">
                <p className="pbh-eyebrow">Start a photo book library</p>
                <h2 className="pbh-h2">One book now. Another when you have more photos.</h2>
                <p className="pbh-body">
                  A photo book doesn’t need to hold every picture you took. Start with one occasion
                  or a set of favorites. A weekend away, the birthday candles, a few
                  blurry-but-beloved moments. A little collection grows from there.
                </p>
                <ul className="pbh-ideas">
                  {IDEAS.map(idea => (
                    <li key={idea}>{idea}</li>
                  ))}
                </ul>
                <div className="pbh-library-actions">
                  <Anchor
                    href={softcover.href}
                    className="pbh-btn"
                    onClick={trackCtaClick('product', 'library', softcover.productId)}
                  >
                    Start with a softcover, from {softcover.price}
                  </Anchor>
                  <Anchor
                    href={MINI_HREF}
                    className="pbh-text-link"
                    onClick={trackCtaClick('mini_ideas', 'library', MINI_PRODUCT)}
                  >
                    Mini photo book ideas
                    <PdpIcon name="arrowRight" size={16} />
                  </Anchor>
                </div>
              </div>
            </Container>
          </section>

          <section className="pbh-section pbh-quotes">
            <Container>
              <div className="pbh-section-head">
                <p className="pbh-eyebrow">From our customers</p>
                <h2 className="pbh-h2">Small enough for a handbag. Made to keep.</h2>
              </div>
              <ul className="pbh-quote-list">
                {QUOTES.map(item => (
                  <li
                    key={item.handle}
                    className={classNames('pbh-quote', { 'pbh-quote--link': item.link })}
                  >
                    <div className="pbh-quote-photo">
                      <Picture
                        imageKey={item.imageKey}
                        prefix={item.prefix}
                        widths={item.widths}
                        sizes="(min-width: 768px) 200px, 44px"
                      />
                    </div>
                    <PdpIcon name="quote" size={24} className="pbh-quote-mark" />
                    <blockquote className="pbh-quote-text">{item.quote}</blockquote>
                    <p className="pbh-quote-author">
                      <span className="pbh-quote-handle">{item.handle} on Instagram</span>
                      <span className="pbh-quote-product">{item.product}</span>
                    </p>
                    {item.link && (
                      <Anchor
                        href={item.link.href}
                        className="pbh-text-link pbh-quote-link"
                        onClick={trackCtaClick('mini_ideas', 'quotes', MINI_PRODUCT)}
                      >
                        {item.link.label}
                        <PdpIcon name="arrowRight" size={16} />
                      </Anchor>
                    )}
                  </li>
                ))}
              </ul>
            </Container>
          </section>

          {content.appPromo && (
            <section className="pbh-promo">
              <Container className="pbh-promo-inner">
                <div className="pbh-promo-copy">
                  <p className="pbh-eyebrow">{content.appPromo.eyebrow}</p>
                  <h2 className="pbh-h2">{content.appPromo.title}</h2>
                  <p className="pbh-body">{content.appPromo.body}</p>
                  <div className="pbh-promo-actions">
                    <div className="pbh-promo-desk">
                      <QrHandoff location="promo" />
                    </div>
                    <div className="pbh-promo-app">
                      <AppStoreBadge location="promo" />
                    </div>
                    <a
                      href={`#${BOOKS_ID}`}
                      className="pbh-arrow-link"
                      onClick={trackCtaClick('web', 'promo')}
                    >
                      Or make it on the web
                      <PdpIcon name="arrowRight" size={16} />
                    </a>
                  </div>
                </div>
                <div className="pbh-promo-photo">
                  <Picture imageKey={image('hero-coffee-table')} sizes="520px" />
                </div>
              </Container>
            </section>
          )}

          {content.extraDescription && (
            <section className="pbh-section pbh-editorial">
              <Container>
                <HtmlContent className="pbh-editorial-body" html={content.extraDescription} />
              </Container>
            </section>
          )}

          {content.faq && (
            <section className="pbh-section pdp-section-orange">
              <Container>
                <ProductFaq title="Questions about photo books" items={content.faq} />
              </Container>
            </section>
          )}

          <section className="pbh-section pbh-closing">
            <Container>
              <div className="pbh-section-head pbh-section-head--center">
                <h2 className="pbh-h2">One finished book is a good beginning</h2>
                <p className="pbh-sub">Start with the photos you want to see in print right now.</p>
              </div>
              <ul className="pbh-closing-list">
                {books.map(book => (
                  <li key={book.productId} className="pbh-closing-item">
                    <span className="pbh-closing-thumb">
                      <Picture imageKey={book.imageKey} sizes="72px" alt="" />
                    </span>
                    <span className="pbh-closing-text">
                      {/* Pseudo element makes the whole row clickable. */}
                      <Anchor
                        href={book.href}
                        className="pbh-closing-link"
                        onClick={trackCtaClick('product', 'closing', book.productId)}
                      >
                        {book.title}
                      </Anchor>
                      <span className="pbh-closing-price">{pricedLabel(book)}</span>
                    </span>
                    <span className="pbh-go" aria-hidden="true">
                      <PdpIcon name="arrowRight" size={16} />
                    </span>
                  </li>
                ))}
              </ul>
            </Container>
          </section>

          <div className={`pbh-sticky-bar${booksInView ? '' : ' visible'}`}>
            <div className="pbh-sticky-text">
              <p className="pbh-sticky-title">Photo books</p>
              <p className="pbh-sticky-meta">{pricedLabel(softcover)} · 26 pages included</p>
            </div>
            <a
              href={`#${BOOKS_ID}`}
              className="pbh-btn"
              onClick={trackCtaClick('choose_book', 'sticky')}
            >
              Choose a book
              <PdpIcon name="arrowUp" size={15} />
            </a>
          </div>
        </div>
      </Layout>
    </>
  );
};

PhotoBooksPage.getInitialProps = async () => ({
  content: await contentApi.category(CATEGORY_ID),
});

PhotoBooksPage.propTypes = {
  content: PropTypes.object.isRequired,
};

export default PhotoBooksPage;
